<template>
    <div class="content-wrapper">
        <section class="content-header">
            <h1>
                {{ $t('brand.brands') }}

                <div class="pull-right">
                    <router-link
                        :to="{ name: 'brands.create' }"
                        class="btn btn-sm btn-primary"
                    >
                        <span class="sr-only">{{ $t('addNew') }}</span>
                        <span class="hidden-xs">{{ $t('addNew') }}</span>
                        <span class="visible-xs"><i class="fa fa-plus"></i></span>
                    </router-link>
                </div>
            </h1>
        </section>

        <section class="content">
            <Alerts />

            <div class="row">
                <div class="col-xs-12">
                    <BrandsFilter />
                    <div class="box box-primary">
                        <div
                            v-if="!isLoaded"
                            class="box-body"
                        >
                            <img
                                class="table-row-preloader center-block"
                                rel="preload"
                                src="/img/table-row-preloader.gif"
                            />
                        </div>

                        <template v-else-if="brands.length">
                            <div class="box-header with-border">
                                <h3 class="box-title">
                                    {{ $t('brand.brandsTable') }}
                                </h3>

                                <div class="box-tools pull-right">
                                    <button
                                        class="btn btn-box-tool"
                                        :title="$t('collapse')"
                                        type="button"
                                        @click="isCollapse = !isCollapse"
                                    >
                                        <i
                                            class="fa"
                                            :class="{
                                                'fa-plus': !isCollapse,
                                                'fa-minus': isCollapse,
                                            }"
                                        />
                                    </button>
                                </div>
                            </div>

                            <vue-slide-up-down :active="isCollapse">
                                <div
                                    v-if="paginationOptions.total"
                                    class="box-body with-border clearfix"
                                >
                                    <BadgeTotal
                                        :value="paginationOptions.total"
                                        color="gray"
                                        :label="`${$t('total')}: `"
                                    />

                                    <div
                                        v-if="paginationOptions.last_page > 1"
                                        class="pagination-box"
                                    >
                                        <PaginationTable
                                            :options="paginationOptions"
                                            @change-page="fetchBrands"
                                        />
                                    </div>
                                </div>

                                <div class="box-body table-responsive no-padding">
                                    <table class="table table-striped table-hover">
                                        <thead>
                                            <tr>
                                                <th>{{ $t('id') }}</th>
                                                <th style="min-width: 120px">
                                                    {{ $t('name') }}
                                                </th>
                                                <th>
                                                    {{ $t('countryType') }}
                                                </th>
                                                <th>
                                                    {{ $t('provider.providers') }}
                                                </th>
                                                <th>
                                                    {{ $t('vertical.verticals') }}
                                                </th>
                                                <th>{{ $t('api.apis') }}</th>
                                                <th>
                                                    {{ $t('offer.offers') }}
                                                </th>
                                                <th class="text-center">
                                                    {{ $t('manualDomains') }}
                                                </th>
                                                <th class="text-center">
                                                    {{ $t('reusableDomains') }}
                                                </th>
                                                <th class="text-nowrap">
                                                    {{ $t('shortDomainTags') }}
                                                </th>
                                                <th class="text-nowrap">
                                                    {{ $t('shortDomains.shortDomains') }}
                                                </th>
                                                <th class="text-center text-nowrap">
                                                    {{ $t('domainLimits') }}
                                                </th>
                                                <th class="text-right text-nowrap">
                                                    {{ $t('createdAt') }}
                                                </th>
                                                <th class="text-right text-nowrap">
                                                    {{ $t('updatedAt') }}
                                                </th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr
                                                v-for="brand in brands"
                                                :key="brand.id"
                                            >
                                                <td>{{ brand.id }}</td>

                                                <td>{{ brand.name }}</td>

                                                <td>{{ brand.countryType.name }}</td>

                                                <td>
                                                    <ValueListEmpty
                                                        :list="brand.providers"
                                                        block
                                                        href="/apis/providers/"
                                                    />
                                                </td>

                                                <td>
                                                    <ValueListEmpty
                                                        :list="brand.verticals"
                                                        block
                                                        href="/apis/verticals/"
                                                    />
                                                </td>

                                                <td>
                                                    <ValueListEmpty
                                                        :list="brand.apis"
                                                        block
                                                        href="/apis/"
                                                    />
                                                </td>

                                                <td>
                                                    <ValueListEmpty
                                                        :list="brand.offers"
                                                        block
                                                        href="/offers/"
                                                    />
                                                </td>

                                                <td class="text-center">
                                                    <ValueYesNo :value="!!brand.domainsManual" />
                                                </td>

                                                <td class="text-center">
                                                    <ValueYesNo :value="!!brand.domainsReusable" />
                                                </td>

                                                <td>
                                                    <ValueListEmpty
                                                        :list="brand.shortDomainsTags"
                                                        block
                                                        href="/short-domains/tags/"
                                                    />
                                                </td>

                                                <td>
                                                    <ValueListEmpty
                                                        :list="brand.shortDomains"
                                                        block
                                                        href="/short-domains/"
                                                    />
                                                </td>

                                                <td class="text-center">
                                                    <ValueYesNo :value="!!brand.domainsLimits" />
                                                </td>

                                                <td class="text-right">
                                                    {{ formatDate(brand.createdAt) }}
                                                </td>

                                                <td class="text-right">
                                                    {{ formatDate(brand.updatedAt) }}
                                                </td>

                                                <td class="text-right text-nowrap">
                                                    <router-link
                                                        :to="{
                                                            name: 'brands.show',
                                                            params: {
                                                                id: brand.id,
                                                            },
                                                        }"
                                                        class="btn btn-sm btn-info"
                                                        :title="$t('show')"
                                                        data-toggle="tooltip"
                                                    >
                                                        <i class="fa fa-eye" />
                                                        <span class="sr-only">
                                                            {{ $t('show') }}
                                                        </span>
                                                    </router-link>

                                                    <router-link
                                                        :to="{
                                                            name: 'brands.edit',
                                                            params: {
                                                                id: brand.id,
                                                            },
                                                        }"
                                                        class="btn btn-sm btn-primary"
                                                        :title="$t('edit')"
                                                        data-toggle="tooltip"
                                                    >
                                                        <i class="fa fa-edit" />
                                                        <span class="sr-only">
                                                            {{ $t('edit') }}
                                                        </span>
                                                    </router-link>

                                                    <button
                                                        type="button"
                                                        class="btn btn-sm btn-danger"
                                                        :title="$t('delete')"
                                                        data-toggle="modal"
                                                        data-target="#delete-modal"
                                                        @click="brandDeleting = brand"
                                                    >
                                                        <i class="fa fa-trash" />
                                                        <span class="sr-only">
                                                            {{ $t('delete') }}
                                                        </span>
                                                    </button>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <div
                                    v-if="paginationOptions.last_page > 1"
                                    class="box-footer clearfix"
                                >
                                    <div class="pagination-box">
                                        <PaginationTable
                                            :options="paginationOptions"
                                            @change-page="fetchBrands"
                                        />
                                    </div>
                                </div>
                            </vue-slide-up-down>
                        </template>
                        <div
                            v-else
                            class="box-body with-border clearfix"
                        >
                            <p class="mb0 text-center">
                                {{ $t('messages.brand.noBrands') }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <BrandsModalDelete
            :brand="brandDeleting"
            @deleted="onDeleted"
        />
    </div>
</template>

<script>
import EventBus from '@/event-bus';
import PaginationTable from '@/components/subcomponents/PaginationTable';
import BadgeTotal from '@/components/subcomponents/BadgeTotal';
import ValueYesNo from '@/components/subcomponents/ValueYesNo';
import ValueListEmpty from '@/components/subcomponents/ValueListEmpty';
import BrandsModalDelete from '@/components/brands/BrandsModalDelete';
import formatsMixin from '@/mixins/formatsMixin';
import BrandsFilter from "@/components/brands/BrandsFilter";

export default {
    components: {
        PaginationTable,
        BadgeTotal,
        ValueYesNo,
        ValueListEmpty,
        BrandsModalDelete,
        BrandsFilter,
    },
    mixins: [formatsMixin],
    data() {
        return {
            isCollapse: true,
            isLoaded: false,
            brandDeleting: {},
            brands: [],
            countryTypes: [],
            paginationOptions: {
                disabled: true,
            },
        };
    },
    created() {
        this.fetchBrands(this.$route.query.page);

        EventBus.$on('relodList', () => {
            this.fetchBrands(this.$route.query.page);
        });
    },
    methods: {
        async fetchBrands(page = 1) {
            this.isLoaded = false;

            try {
                this.$router
                    .replace({
                        query: {
                            ...this.$route.query,
                            page: page > 1 ? page : undefined,
                        },
                    })
                    .catch(() => {});

                this.paginationOptions.disabled = true;

                const {
                    data: {
                        brands: { data: brands, ...paginationOptions },
                        countryTypes,
                    },
                } = await axios.get('/brands/data', {
                    params: {
                        page: page,
                        filters: {
                            search: this.$route.query['filter[search]'],
                        },
                    },
                });

                this.countryTypes = countryTypes;
                this.brands = brands.map((brand) => {
                    brand.countryType = countryTypes.find((e) => e.id === brand.countryType);
                    return brand;
                });
                this.paginationOptions = paginationOptions;
            } catch (error) {
                EventBus.$emit('alertData', {
                    class: 'alert-danger',
                    message: error.response.data.message,
                    errors: error.response.data.errors,
                });
            } finally {
                this.isLoaded = true;
                this.paginationOptions.disabled = false;
            }
        },
        onDeleted() {
            this.fetchBrands();
        },
    },
};
</script>
